import { PROVIDER_API, USE_MOCK_AUTH, portfolioGroupRecordsPath, portfolioUploadRecordsPath } from "../config/api";
import { displayPersonName } from "../utils/personName";
import { formatGhanaPhoneLocal } from "../utils/ghanaPhone";
import { daysLateFromDueDate } from "../utils/calendarDate";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

export function normalizeFileUpload(raw) {
  if (!raw || typeof raw !== "object") return null;

  const pass = Number(raw.number_of_cases ?? raw.validation_pass ?? raw.validationPass ?? raw.accounts ?? 0);
  const invalid = Number(raw.invalid_rows ?? raw.invalidRows ?? 0);
  const totalRows = pass + invalid;
  const totalAmount = Number(raw.total_amount ?? raw.totalOwed ?? raw.total_owed ?? 0);

  return {
    id: String(raw.id ?? raw.upload_slug ?? ""),
    numericId: raw.id ?? null,
    portfolio_group_slug: raw.group_id != null ? String(raw.group_id) : raw.portfolio_group_slug || raw.group_slug || null,
    fileName: raw.title || raw.file_name || raw.fileName || "Upload",
    accounts: pass,
    totalOwed: totalAmount,
    uploadedAt: raw.created_at || raw.uploaded_at || raw.uploadedAt || null,
    updatedAt: raw.updated_at || raw.updatedAt || null,
    status: raw.status === "failed" || raw.failed_at ? "failed" : "completed",
    stage: "cases_created",
    casesCreated: pass,
    validationPass: pass,
    invalidRows: invalid,
    validationPassRate: totalRows > 0 ? (pass / totalRows) * 100 : pass > 0 ? 100 : 0,
  };
}

function extractUploads(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.file_uploads)) return data.file_uploads;
  if (Array.isArray(data?.uploads)) return data.uploads;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export function attachUploadsToGroups(groups = [], uploads = []) {
  const bySlug = new Map();
  uploads.forEach((upload) => {
    const key = upload.portfolio_group_slug;
    if (!key) return;
    const list = bySlug.get(key) || [];
    list.push(upload);
    bySlug.set(key, list);
  });

  return groups.map((group) => {
    const slug = String(group.group_slug || group.id);
    const embedded = Array.isArray(group.uploads) ? group.uploads : [];
    const fromList = bySlug.get(slug);
    const uploads = fromList?.length ? fromList : embedded;
    return { ...group, uploads };
  });
}

export function normalizeUploadRecord(raw) {
  if (!raw || typeof raw !== "object") return null;

  const nested = raw.customer && typeof raw.customer === "object" ? raw.customer : null;
  const amountOwed = Number(raw.amount_owed ?? raw.loan_amount ?? raw.amountOwed ?? raw.outstanding_balance ?? 0);
  const amountPaid = Number(raw.amount_paid ?? raw.amountPaid ?? 0);
  const outstanding = Number(
    raw.outstanding ?? raw.outstanding_balance ?? raw.outstanding_amount ?? Math.max(0, amountOwed - amountPaid)
  );
  const dueDate = raw.loan_due_date || raw.due_date || raw.dueDate || null;
  const daysRaw = raw.days_overdue ?? raw.days_late ?? raw.daysOverdue ?? raw.daysLate;
  const daysFromApi = daysRaw == null || daysRaw === "" ? null : Number(daysRaw);
  const daysLate = Number.isFinite(daysFromApi)
    ? Math.max(0, daysFromApi)
    : daysLateFromDueDate(dueDate);

  return {
    id: String(raw.id ?? raw.case_id ?? raw.account_number ?? ""),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    case_id: raw.case_code || raw.case_id || raw.caseId || null,
    customer_number: formatGhanaPhoneLocal(raw.customer_number || raw.customerNumber || raw.phone_number || raw.phone || "") || raw.customer_number || raw.customerNumber || raw.phone_number || raw.phone || "",
    first_name: raw.first_name || nested?.first_name || "",
    last_name: raw.last_name || nested?.last_name || "",
    fullname: raw.fullname || nested?.fullname || "",
    customer_name: displayPersonName(
      { first_name: raw.first_name || nested?.first_name, last_name: raw.last_name || nested?.last_name },
      raw.fullname,
      raw.customer_name,
      nested?.fullname,
      typeof raw.customer === "string" ? raw.customer : "",
      raw.customerName
    ),
    case_code: raw.case_code || raw.case_id || null,
    account_number: raw.account_number || raw.accountNumber || "",
    phone: formatGhanaPhoneLocal(raw.phone_number || raw.phone || "") || raw.phone_number || raw.phone || "",
    email: raw.email || nested?.email || null,
    region: raw.region || nested?.region || null,
    country: raw.country || nested?.country || null,
    exact_area: raw.exact_area || raw.exactArea || nested?.location || null,
    gps_address: raw.gps_address || raw.gpsAddress || nested?.gps_address || null,
    gender: raw.gender || nested?.gender || null,
    date_of_birth: raw.date_of_birth || raw.dateOfBirth || nested?.date_of_birth || null,
    ghana_card_number: raw.ghana_card_number || raw.ghanaCardNumber || nested?.ghanacard_id || null,
    loan_amount: Number(raw.loan_amount ?? raw.loanAmount ?? 0),
    last_payment_date: raw.last_payment_date || raw.lastPaymentDate || null,
    amount_owed: Number.isFinite(amountOwed) ? amountOwed : 0,
    amount_paid: Number.isFinite(amountPaid) ? amountPaid : 0,
    outstanding: Number.isFinite(outstanding) ? outstanding : 0,
    outstanding_amount: Number.isFinite(outstanding) ? outstanding : 0,
    due_date: dueDate,
    days_overdue: daysLate,
    days_late: daysLate,
    daysLate,
    group_name: raw.group_name || null,
    portfolio_slug: raw.portfolio_slug || null,
    portfolio_group_slug: raw.portfolio_group_slug || null,
    status: raw.status || null,
    created_at: raw.created_at || null,
    updated_at: raw.updated_at || null,
  };
}

function extractRecords(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.cases)) return data.cases;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

/**
 * GET /api/provs/portfolios/file-uploads
 */
let uploadsInFlight = { token: null, promise: null };
let groupRecordsInFlight = new Map();

export async function fetchPortfolioFileUploads(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  if (!force && uploadsInFlight.token === token && uploadsInFlight.promise) {
    return uploadsInFlight.promise;
  }

  const promise = (async () => {
    const json = await apiRequest(PROVIDER_API.portfolioFileUploads, { method: "GET", token });
    const data = unwrapApiEnvelope(json);
    return extractUploads(data)
      .map(normalizeFileUpload)
      .filter(Boolean)
      .sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0));
  })();

  uploadsInFlight = { token, promise };
  return promise;
}

export function clearPortfolioUploadsCache() {
  uploadsInFlight = { token: null, promise: null };
  groupRecordsInFlight = new Map();
}
async function fetchAllRecordPages(token, path) {
  const rows = [];
  let page = 1;
  let lastPage = 1;

  do {
    const sep = path.includes("?") ? "&" : "?";
    const json = await apiRequest(`${path}${sep}page=${page}&limit=1000`, {
      method: "GET",
      token,
    });
    const data = unwrapApiEnvelope(json);
    rows.push(...extractRecords(data));
    lastPage = Number(data?.pagination?.last_page || 1);
    page += 1;
  } while (page <= lastPage && page <= 40);

  return rows.map(normalizeUploadRecord).filter(Boolean);
}

/**
 * GET /api/provs/portfolio-groups/{group_slug}/uploads/{upload_id}/records
 */
export async function fetchPortfolioUploadRecords(token, groupSlug, uploadId) {
  if (!groupSlug || !uploadId) return [];
  if (USE_MOCK_AUTH || !token) return null;

  return fetchAllRecordPages(token, portfolioUploadRecordsPath(groupSlug, uploadId));
}

/**
 * All cases/records for a portfolio group.
 * Prefers GET …/portfolio-groups/{slug}/records; falls back to merging each upload’s records.
 */
export async function fetchPortfolioGroupRecords(token, groupSlug, uploads = [], { force = false } = {}) {
  if (!groupSlug) return [];
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `${token}:${groupSlug}`;
  if (!force && groupRecordsInFlight.has(cacheKey)) {
    return groupRecordsInFlight.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const rows = await fetchAllRecordPages(token, portfolioGroupRecordsPath(groupSlug));
      if (rows.length || !uploads.length) return rows;
    } catch {
      // Fall through — older backends may only expose per-upload records.
    }

    const results = await Promise.all(
      uploads.map(async (upload) => {
        const uploadId = upload?.id;
        if (!uploadId) return [];
        try {
          const rows = await fetchPortfolioUploadRecords(token, groupSlug, uploadId);
          return (rows || []).map((row) => ({
            ...row,
            upload_id: String(uploadId),
            file_name: upload.fileName || upload.file_name || null,
          }));
        } catch {
          return [];
        }
      })
    );

    return results.flat();
  })();

  groupRecordsInFlight.set(cacheKey, promise);
  return promise;
}

/**
 * POST /api/provs/portfolios/upload
 * Form data: file, portfolio_group_slug
 */
export async function uploadPortfolioFile(token, { file, portfolioGroupSlug }) {
  if (!file) throw new Error("Choose an Excel or CSV file to upload.");
  if (!portfolioGroupSlug) throw new Error("Portfolio group is required.");

  if (USE_MOCK_AUTH || !token) {
    return {
      file_upload: normalizeFileUpload({
        id: Date.now(),
        portfolio_group_slug: portfolioGroupSlug,
        title: file.name,
        total_amount: 0,
        validation_pass: 0,
        invalid_rows: 0,
        created_at: new Date().toISOString(),
      }),
      portfolio_group: null,
    };
  }

  const body = new FormData();
  body.append("file", file);
  body.append("portfolio_group_slug", portfolioGroupSlug);

  const json = await apiRequest(PROVIDER_API.portfolioUpload, {
    method: "POST",
    token,
    body,
  });
  const data = unwrapApiEnvelope(json);

  return {
    file_upload: normalizeFileUpload(data?.file_upload || data?.upload || data),
    portfolio_group: data?.portfolio_group || null,
  };
}
